import { v4 as uuidv4 } from "uuid";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { primaryBlockDefaultStyles } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/stylesHelpers";
import {
  createButtonBlock,
  createH2Block,
  createPBlock,
  createUlBlock,
} from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/textBlocksHelpers";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { getBackgroundImageOptions } from "@circle-react-shared/BuilderUI/utils/backgroundHelpers";
import {
  getMarginOptions,
  getPaddingOptions,
} from "@circle-react-shared/BuilderUI/utils/boxModelStyleHelpers";
import { getBackgroundColorOptions } from "@circle-react-shared/BuilderUI/utils/buttonStyleHelpers";
import { getSizingOptions } from "@circle-react-shared/BuilderUI/utils/sizeStyleHelpers";
import { getFontWeightOptions } from "@circle-react-shared/BuilderUI/utils/textStyleHelpers";
import checkBullet from "./check_bullet.png";

const bulletStyles = {
  margin: getMarginOptions({
    defaultValue: {
      marginTop: "4px",
      marginBottom: "4px",
      marginLeft: "0px",
      marginRight: "0px",
    },
  }),
};

export const defaultData: BaseBlock = {
  id: uuidv4(),
  type: "PlanCardBlock",
  props: {
    header: {
      prop: "children",
      type: "children",
      value: [
        createPBlock({
          text: "All Access Plan",
          styles: {
            fontWeight: getFontWeightOptions("600"),
          },
        }),
      ],
    },
    price: {
      prop: "children",
      type: "children",
      value: [
        createH2Block({
          text: "$89",
          styles: {
            fontWeight: getFontWeightOptions("500"),
          },
        }),
        createPBlock({
          text: "/month",
          paragraphStyle: "p3",
        }),
      ],
    },
    button: {
      prop: "children",
      type: "children",
      value: [
        createButtonBlock({
          text: "Get access",
          href: "#",
          styles: {
            sizing: getSizingOptions({
              defaultValue: {
                width: "100%",
                height: "40px",
                minWidth: "100px",
                minHeight: "40px",
                maxWidth: "none",
                maxHeight: "none",
              },
            }),
          },
        }),
      ],
    },
    content: {
      prop: "children",
      type: "children",
      value: [
        createPBlock({
          text: "What's included:",
          paragraphStyle: "p3",
          styles: {
            fontWeight: getFontWeightOptions("500"),
          },
        }),
        createUlBlock({
          items: [
            {
              text: "5 Core modules",
              placeholder: checkBullet,
              bulletStyles,
            },
            {
              text: "10+ Video lessons",
              placeholder: checkBullet,
              bulletStyles,
            },
            {
              text: "Notion worksheet templates",
              placeholder: checkBullet,
              bulletStyles,
            },
            {
              text: "Private community access",
              placeholder: checkBullet,
              bulletStyles,
            },
            {
              text: "Monthly live Q&A sessions",
              placeholder: checkBullet,
              bulletStyles,
            },
          ],
        }),
      ],
    },
    styles: {
      prop: "styles",
      type: "styles",
      value: {
        backgroundColor: getBackgroundColorOptions({
          defaultValue: primaryBlockDefaultStyles.backgroundColor,
        }),
        backgroundImage: getBackgroundImageOptions(),
        padding: getPaddingOptions({
          defaultValue: {
            paddingTop: "32",
            paddingBottom: "32",
            paddingLeft: "32",
            paddingRight: "32",
          },
        }),
        margin: getMarginOptions({
          defaultValue: primaryBlockDefaultStyles.margin,
        }),
      },
    },
  },
};

export const createPlanCardBlock = ({
  title,
  price,
  priceSuffix,
  button,
  listTitle,
  features,
  styles = {},
}: {
  title: string;
  price: string;
  priceSuffix: string;
  button: { text: string; href: string };
  listTitle: string;
  features: string[];
  styles?: Record<string, StyleValue>;
}): BaseBlock => ({
  ...defaultData,
  id: uuidv4(),
  type: "PlanCardBlock",
  props: {
    ...defaultData.props,
    header: {
      prop: "children",
      type: "children",
      value: [
        createPBlock({
          text: title,
          styles: {
            fontWeight: getFontWeightOptions("600"),
          },
        }),
      ],
    },
    price: {
      prop: "children",
      type: "children",
      value: [
        createH2Block({
          text: price,
          styles: {
            fontWeight: getFontWeightOptions("500"),
          },
        }),
        createPBlock({
          text: priceSuffix,
          paragraphStyle: "p3",
        }),
      ],
    },
    button: {
      prop: "children",
      type: "children",
      value: [
        createButtonBlock({
          text: button.text,
          href: button.href,
          styles: {
            sizing: getSizingOptions({
              defaultValue: {
                width: "100%",
                height: "40px",
                minWidth: "100px",
                minHeight: "40px",
                maxWidth: "none",
                maxHeight: "none",
              },
            }),
          },
        }),
      ],
    },
    content: {
      prop: "children",
      type: "children",
      value: [
        createPBlock({
          text: listTitle,
          paragraphStyle: "p3",
          styles: {
            fontWeight: getFontWeightOptions("500"),
          },
        }),
        createUlBlock({
          items: features.map(feature => ({
            text: feature,
            placeholder: checkBullet,
            bulletStyles,
          })),
        }),
      ],
    },
    styles: {
      prop: "styles",
      type: "styles",
      value: {
        ...(defaultData.props.styles.value || {}),
        ...styles,
      },
    },
  },
});
